import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/Card';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import { updateUserProfile } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

function EditProfileModal({ isOpen, onClose, initialData, onSuccess }) {
    const { participanteId } = useAuth();
    const modalRef = useRef(null); // Referência para o modal
    const initialFocusRef = useRef(null); // Referência para o primeiro input

    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        grauAcademico: '', // NOVO CAMPO
        instituicao: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen && initialData) {
            setFormData({
                nome: initialData.nome || '',
                email: initialData.email || '',
                grauAcademico: initialData.grauAcademico || '', // ATUALIZADO
                instituicao: initialData.instituicao || '',
            });
            setError('');
            
            // Define o foco no primeiro input quando o modal abre
            setTimeout(() => {
                if (initialFocusRef.current) {
                    initialFocusRef.current.focus();
                }
            }, 100); // Pequeno delay para garantir que o modal esteja renderizado
        }
    }, [isOpen, initialData]);

    if (!isOpen) {
        return null;
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        if (!participanteId) {
            setError('ID do participante não encontrado. Por favor, faça login novamente.');
            setIsSubmitting(false);
            return;
        }

        try {
            await updateUserProfile(participanteId, formData);
            toast.success(`Seu perfil foi atualizado com sucesso!`);
            onSuccess();
        } catch (err) {
            console.error('Erro ao atualizar perfil:', err);
            setError(err.message || 'Ocorreu um erro ao tentar atualizar seu perfil.');
            toast.error(err.message || 'Erro ao atualizar perfil.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div 
            className="fixed inset-0 bg-black/60 z-50 flex justify-center items-center p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="edit-profile-title"
            ref={modalRef}
        >
            <Card className="w-full max-w-md relative">
                <button 
                    onClick={onClose} 
                    className="absolute top-4 right-4 p-1 rounded-full hover:bg-accent transition-colors"
                    aria-label="Fechar"
                >
                    <X className="h-5 w-5" />
                </button>
                <CardHeader>
                    <CardTitle id="edit-profile-title" className="mb-2">Editar Meu Perfil</CardTitle>
                    <CardDescription className="leading-relaxed">
                        Atualize os seus detalhes de perfil.
                    </CardDescription>
                </CardHeader>
                <form onSubmit={handleSubmit}>
                    <CardContent className="space-y-4">
                        {error && <p className="text-sm font-medium text-destructive">{error}</p>}

                        <div className="grid gap-2">
                            <Label htmlFor="nome" className="mb-1">Nome Completo</Label>
                            <Input 
                                id="nome" 
                                name="nome" 
                                value={formData.nome} 
                                onChange={handleChange} 
                                required 
                                disabled={isSubmitting} 
                                ref={initialFocusRef} // Define o foco inicial aqui
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="email" className="mb-1">Email</Label>
                            <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required disabled={isSubmitting} />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="grauAcademico" className="mb-1">Grau Acadêmico (Opcional)</Label> {/* ATUALIZADO */}
                            <Input id="grauAcademico" name="grauAcademico" value={formData.grauAcademico} onChange={handleChange} disabled={isSubmitting} />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="instituicao" className="mb-1">Instituição (Opcional)</Label>
                            <Input id="instituicao" name="instituicao" value={formData.instituicao} onChange={handleChange} disabled={isSubmitting} />
                        </div>
                    </CardContent>
                    <CardFooter className="flex flex-col sm:flex-row justify-end gap-2">
                        <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting} className="w-full sm:w-auto">
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto">
                            {isSubmitting ? 'A guardar...' : 'Guardar Alterações'}
                        </Button>
                    </CardFooter>
                </form>
            </Card>
        </div>
    );
}

export default EditProfileModal;